// ScanLookupView — the "looking" loader card and the propose-canonical
// popup for the scan pipeline. Extracted verbatim from ScanPipeline.jsx
// (pure move).

import { useMemo, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { enrichIngredient } from "../../../lib/enrichIngredient";
import { FONT, COLOR, CHIP_TONES } from "../../../lib/tokens";
import {
  Header, RawScanPanel, formatUpc, confidenceColor,
  primaryActionStyle, secondaryActionStyle, ghostActionStyle,
} from "./scanShared";

// ── Looking phase — animated barcode loader ─────────────────────────
// Twelve vertical bars of varying widths with a glowing horizontal
// scan line sweeping across them. Reads as "actively decoding"
// rather than a generic spinner.

export function LookingCard({ upc }) {
  const bars = useMemo(() => {
    // Deterministic bar widths so the same UPC always renders the
    // same barcode shape — feels like the scanner "remembered" what
    // it just read instead of regenerating noise.
    const seed = (upc && Number(upc.slice(-6))) || 314159;
    return Array.from({ length: 28 }, (_, i) => {
      const r = ((seed * (i + 1) * 9301 + 49297) % 233280) / 233280;
      const w = 2 + Math.floor(r * 6); // 2..7 px
      const tall = i % 6 === 0;
      return { w, tall };
    });
  }, [upc]);

  return (
    <div style={lookingShellStyle}>
      <div style={lookingKickerStyle}>READING</div>
      <div style={lookingBarcodeStyle}>
        <div style={lookingBarsRow}>
          {bars.map((b, i) => (
            <div
              key={i}
              style={{
                width: b.w,
                height: b.tall ? 92 : 76,
                background: COLOR.ink,
                borderRadius: 1,
                animation: `scan-bar-fade 1.3s ${(i * 30) % 800}ms ease-in-out infinite`,
              }}
            />
          ))}
        </div>
        <div style={lookingScanLineStyle} />
      </div>
      <div style={lookingDigitsStyle}>{upc ? formatUpc(upc) : "—"}</div>
      <div style={lookingHintStyle}>
        Household memory · Open Food Facts · USDA · Haiku verifying…
      </div>
      <style>{`
        @keyframes scan-bar-fade {
          0%, 100% { opacity: 0.85; }
          50%      { opacity: 0.35; }
        }
        @keyframes scan-line-sweep {
          0%   { transform: translateX(-110%); opacity: 0.0; }
          12%  { opacity: 1; }
          88%  { opacity: 1; }
          100% { transform: translateX(110%); opacity: 0.0; }
        }
        @keyframes scan-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ── Propose canonical — popup with raw data + AI suggestion ─────────
// On mount, the card calls enrichIngredient (Haiku) with the OFF
// productName as the seed. Haiku interprets messy marketing strings
// ("Slim Jim Buffalo Wild Wings Cheese Frank") into a clean canonical
// name + full enrichment row. The user sees the suggestion in an
// editable field, can rename, and confirms — at which point we bind
// the slug and the review surface picks up the freshly-minted row.

export function ProposeCanonicalCard({ rawScan, aiSuggestion, userId, onConfirm, onSkip, onRescan, onPhoto }) {
  const seedName = rawScan?.merged?.productName || rawScan?.off?.productName || "";

  // The Haiku verify-or-propose call has already run in the parent's
  // lookup phase (see ScanPipeline.handleDetected). If `aiSuggestion`
  // is provided, we pre-fill from it; otherwise (rare — Haiku errored
  // or skipped) we fall back to a programmatic flavor/brand strip.
  const initialName = aiSuggestion?.name || programmaticGuess(seedName);
  const [name, setName]                  = useState(initialName);
  const [submitting, setSubmitting]      = useState(false);
  const [confirmError, setConfirmError]  = useState(null);

  const trimmed = name.trim();
  const canConfirm = trimmed.length >= 2 && !submitting;
  const aiNamePresent  = !!aiSuggestion?.name;
  const userEditedAi   = aiNamePresent && trimmed.toLowerCase() !== aiSuggestion.name.toLowerCase();

  async function handleConfirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    setConfirmError(null);
    try {
      // SKIP RE-ENRICH WHEN ALREADY EXISTS — if the user typed a name
      // that already has an ingredient_info row (e.g. they're re-
      // confirming "Gjetost" after a prior scan minted it), bind the
      // existing slug instead of firing enrichIngredient AGAIN. The
      // re-call would upsert the same row, costing a Haiku call for
      // no functional change. Lookup is a single-row fetch by
      // ingredient_id (the slug); supabase RLS allows public read on
      // ingredient_info so no auth needed.
      const candidateSlug = aiSuggestion?.slug || slugifyClient(trimmed);
      if (candidateSlug) {
        const { data: existing } = await supabase
          .from("ingredient_info")
          .select("ingredient_id, info")
          .eq("ingredient_id", candidateSlug)
          .maybeSingle();
        if (existing?.ingredient_id) {
          const displayName = existing.info?.name || trimmed;
          onConfirm?.(existing.ingredient_id, displayName);
          return;
        }
      }

      // No existing row — fire the heavy AI enrichment call.
      // enrichIngredient writes a pending_ingredient_info row with
      // full enrichment. The edge fn reads userId from the JWT; no
      // client-side userId pre-check needed.
      const r = await enrichIngredient({ source_name: trimmed });
      const canonicalSlug = r?.pending?.slug;
      const displayName   = r?.pending?.info?.name || trimmed;
      if (!canonicalSlug) throw new Error("No slug produced.");
      onConfirm?.(canonicalSlug, displayName);
    } catch (err) {
      setConfirmError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function slugifyClient(raw) {
    return String(raw || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  }

  // The dead-end branch — aiSuggestion is null — covers two paths:
  // (a) Haiku was never called because there was no signal to send
  //     (no productName, no ingredientsText), and (b) Haiku errored
  //     mid-call. The user's options are the same in both cases, so
  //     the copy doesn't try to distinguish them — it just names the
  //     three real exits: type a name, take a photo, scan a different
  //     code. "Haiku didn't respond" was a small lie in case (a).
  const kickerLabel = !aiSuggestion
    ? "NO MATCH"
    : aiSuggestion.supersedesTentative
      ? "MORE SPECIFIC"
      : "HAIKU SUGGESTS";

  const explainer = !aiSuggestion
    ? "Nothing on file for this barcode yet — no Open Food Facts entry, no household memory. Tell us what it is, or take a photo and we'll read the package for you."
    : aiSuggestion.supersedesTentative
      ? "Our registry's word-match was too generic for this product. Haiku reread the scan and suggests something more specific."
      : "Edit if it's wrong. Confirm to use this item today and queue it for admin review.";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Header
        upc={rawScan?.upc}
        kicker="NEW CANONICAL"
        title={aiSuggestion?.supersedesTentative ? "Too generic — let's get specific" : "No match — what is this?"}
      />

      <RawScanPanel rawScan={rawScan} draft={null} startOpen />

      <div style={proposeCardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 700,
            color: CHIP_TONES.canonical.fg, letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}>
            {kickerLabel}
          </span>
          {aiSuggestion?.confidence && (
            <span style={{
              fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
              color: confidenceColor(aiSuggestion.confidence),
              letterSpacing: "0.14em", textTransform: "uppercase",
              padding: "1px 6px",
              border: `1px solid ${confidenceColor(aiSuggestion.confidence)}`,
              borderRadius: 999,
            }}>
              {aiSuggestion.confidence} confidence
            </span>
          )}
        </div>
        <div style={{
          fontFamily: FONT.detail, fontStyle: "italic", fontSize: 13,
          color: COLOR.muted, lineHeight: 1.4,
        }}>
          {explainer}
        </div>
        {aiSuggestion?.reasoning && (
          <div style={reasoningStyle}>
            <span style={{ fontWeight: 700, marginRight: 6 }}>WHY:</span>
            {aiSuggestion.reasoning}
          </div>
        )}
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Canonical name…"
          style={proposeInputStyle}
          disabled={submitting}
        />
        {/* "Demoted to claims" pills row removed per repeated user
            request. Claims still land on store.meta.claims and
            round-trip on commit — they're just not surfaced visually
            on the propose card anymore. */}
        {userEditedAi && (
          <div style={{
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 500,
            color: COLOR.muted, letterSpacing: "0.06em",
          }}>
            You edited Haiku's suggestion — confirming saves under your typed name.
          </div>
        )}
        {confirmError && (
          <div style={errorBannerStyle}>{confirmError}</div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="mcm-focusable"
            style={{ ...primaryActionStyle(canConfirm), flex: 2 }}
          >
            {submitting ? "SAVING…" : `USE "${trimmed.toUpperCase() || "—"}"`}
          </button>
          <button
            type="button"
            onClick={() => onSkip?.(trimmed)}
            disabled={submitting}
            className="mcm-focusable"
            style={{ ...secondaryActionStyle, flex: 1 }}
          >
            Skip
          </button>
        </div>
        {onPhoto && (
          <button
            type="button"
            onClick={onPhoto}
            disabled={submitting}
            className="mcm-focusable"
            style={photoActionStyle}
          >
            Take a photo of the package instead
          </button>
        )}
        <button type="button" onClick={onRescan} className="mcm-focusable" style={ghostActionStyle}>
          Scan a different barcode
        </button>
      </div>
    </div>
  );
}

export function programmaticGuess(productName) {
  // Last-resort fallback when Haiku is silent. Drop obvious flavor /
  // size / packaging tokens from the marketing string. Not great —
  // that's why Haiku is the primary path — but better than empty.
  if (!productName) return "";
  return String(productName)
    .replace(/\b(\d+(\.\d+)?\s*(oz|lb|g|kg|ml|fl\s*oz|l|ct|count|pack)\b)/gi, "")
    .replace(/\b(original|classic|premium|extra|new|natural|organic|fresh)\b/gi, "")
    .replace(/[(),]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const lookingShellStyle = {
  display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
  padding: "26px 4px 12px",
};

const lookingKickerStyle = {
  fontFamily: FONT.forma, fontSize: 10, fontWeight: 700,
  color: COLOR.muted, letterSpacing: "0.22em", textTransform: "uppercase",
  animation: "scan-pulse 1.2s ease-in-out infinite",
};

const lookingBarcodeStyle = {
  position: "relative",
  padding: "12px 16px",
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10,
  overflow: "hidden",
};

const lookingBarsRow = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
};

const lookingScanLineStyle = {
  position: "absolute",
  top: 6, bottom: 6, left: 0,
  width: 24,
  background: "linear-gradient(90deg, transparent, rgba(217,107,43,0.85), transparent)",
  filter: "blur(1px)",
  animation: "scan-line-sweep 1.6s linear infinite",
  pointerEvents: "none",
};

const lookingDigitsStyle = {
  fontFamily: FONT.forma, fontSize: 14, fontWeight: 600,
  color: COLOR.ink, letterSpacing: "0.18em",
};

const lookingHintStyle = {
  fontFamily: FONT.detail, fontStyle: "italic", fontSize: 13, color: COLOR.muted,
  textAlign: "center",
};

const errorBannerStyle = {
  padding: "10px 12px",
  background: COLOR.roseDeep,
  border: `1px solid ${COLOR.roseDim}`,
  borderRadius: 10,
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
  color: COLOR.rose, lineHeight: 1.45,
};

// The propose-card photo CTA — sits between primary "USE …" and the
// muted "Scan a different barcode" ghost. Green tone signals forward
// progress (per the progress=green/warning=yellow rule); leaf-deep
// fill keeps it clearly secondary to the primary dark CTA above.
const photoActionStyle = {
  width: "100%", padding: "12px",
  background: COLOR.leafDeep,
  color: COLOR.leaf,
  border: `1px solid ${COLOR.leafDim}`,
  borderRadius: 12,
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  letterSpacing: "0.10em",
  cursor: "pointer", touchAction: "manipulation",
};

const proposeCardStyle = {
  display: "flex", flexDirection: "column", gap: 10,
  padding: "14px",
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 14,
};

const proposeInputStyle = {
  width: "100%", padding: "12px 14px",
  fontFamily: FONT.detail, fontStyle: "italic", fontWeight: 500,
  fontSize: 17, color: COLOR.ink,
  background: COLOR.deep,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10, outline: "none",
  boxSizing: "border-box",
  WebkitAppearance: "none",
};

const reasoningStyle = {
  fontFamily: FONT.forma, fontSize: 11, fontWeight: 500,
  color: COLOR.dim, lineHeight: 1.45,
  padding: "8px 10px",
  background: COLOR.deep,
  border: `1px dashed ${COLOR.border}`,
  borderRadius: 8,
  letterSpacing: "0.02em",
};
